import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, Plus, Upload, LogOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Seo } from "../components/site/Seo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const EMPTY_PRODUCT = { name: "", category: "Alimentos", line: "", image: "", presentation: "", description: "", order: 0 };
const EMPTY_ARTICLE = { title: "", excerpt: "", image: "", content: "", meta_description: "" };
const CATEGORIES = ["Alimentos", "Cuidado del agua", "Accesorios"];

function ImageField({ value, onChange, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form);
      onChange(data.url);
      toast.success("Imagen subida correctamente");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL de la imagen o sube un archivo"
          className="flex-1 px-3 py-2.5 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          data-testid={`${testid}-image-input`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-950 hover:bg-brand-600 disabled:opacity-60 text-white text-xs font-bold transition-colors"
          data-testid={`${testid}-upload-button`}
        >
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Subiendo…" : "Subir imagen"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file-input`} />
      </div>
      {value && (
        <img src={value} alt="Vista previa" className="h-20 w-20 object-contain rounded-lg border border-zinc-200 bg-zinc-50" data-testid={`${testid}-image-preview`} />
      )}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500";
const labelCls = "text-xs font-bold text-zinc-700";

export default function AdminDashboard({ auth, onLogout }) {
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [productForm, setProductForm] = useState(null); // null | {data, slug?}
  const [articleForm, setArticleForm] = useState(null);
  const [toDelete, setToDelete] = useState(null); // {type, slug, name}

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      if (productForm.slug) {
        await api.put(`/admin/products/${productForm.slug}`, productForm.data);
        toast.success("Producto actualizado");
      } else {
        await api.post("/admin/products", productForm.data);
        toast.success("Producto creado");
      }
      setProductForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    try {
      if (articleForm.slug) {
        await api.put(`/admin/articles/${articleForm.slug}`, articleForm.data);
        toast.success("Artículo actualizado");
      } else {
        await api.post("/admin/articles", articleForm.data);
        toast.success("Artículo creado");
      }
      setArticleForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/${toDelete.type}/${toDelete.slug}`);
      toast.success("Eliminado correctamente");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
  };

  return (
    <main className="min-h-screen bg-zinc-100" data-testid="admin-dashboard">
      <Seo title="Panel de administración — AZOO Costa Rica" description="Gestión de productos y artículos de AZOO Costa Rica." />
      <div className="bg-zinc-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo-azoo.png" alt="AZOO" className="h-7 w-auto bg-white rounded px-1.5 py-0.5" />
            <span className="text-sm font-bold">Administración</span>
            <span className="hidden sm:inline text-xs text-zinc-400">{auth.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs font-bold transition-colors" data-testid="admin-view-site">
              <ExternalLink className="w-3.5 h-3.5" /> Ver sitio
            </Link>
            <button onClick={handleLogout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-red-600 text-xs font-bold transition-colors" data-testid="admin-logout-button">
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="productos">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="productos" data-testid="tab-productos">Productos ({products.length})</TabsTrigger>
            <TabsTrigger value="articulos" data-testid="tab-articulos">Artículos ({articles.length})</TabsTrigger>
          </TabsList>

          {/* PRODUCTOS */}
          <TabsContent value="productos" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-lg font-black text-zinc-950">Catálogo de productos</h1>
              <button
                onClick={() => setProductForm({ data: { ...EMPTY_PRODUCT } })}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold uppercase tracking-wider transition-colors"
                data-testid="new-product-button"
              >
                <Plus className="w-4 h-4" /> Nuevo producto
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm" data-testid="products-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                    <th className="px-4 py-3">Producto</th>
                    <th className="px-4 py-3">Categoría</th>
                    <th className="px-4 py-3">Línea</th>
                    <th className="px-4 py-3">Presentación</th>
                    <th className="px-4 py-3">Orden</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.slug} className="border-b border-zinc-100 last:border-0" data-testid={`product-row-${p.slug}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {p.image && <img src={p.image} alt="" className="w-10 h-10 object-contain rounded bg-zinc-50 border border-zinc-100" />}
                          <span className="font-semibold text-zinc-900 text-xs">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs">{p.category}</td>
                      <td className="px-4 py-3 text-xs">{p.line}</td>
                      <td className="px-4 py-3 text-xs">{p.presentation}</td>
                      <td className="px-4 py-3 text-xs">{p.order}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => setProductForm({ data: { name: p.name, category: p.category, line: p.line, image: p.image, presentation: p.presentation, description: p.description, order: p.order }, slug: p.slug })} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-600" aria-label="Editar" data-testid={`edit-product-${p.slug}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => setToDelete({ type: "products", slug: p.slug, name: p.name })} className="p-2 rounded-lg hover:bg-red-50 text-red-500" aria-label="Eliminar" data-testid={`delete-product-${p.slug}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ARTÍCULOS */}
          <TabsContent value="articulos" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-lg font-black text-zinc-950">Artículos del blog</h1>
              <button
                onClick={() => setArticleForm({ data: { ...EMPTY_ARTICLE } })}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold uppercase tracking-wider transition-colors"
                data-testid="new-article-button"
              >
                <Plus className="w-4 h-4" /> Nuevo artículo
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3" data-testid="articles-list">
              {articles.map((a) => (
                <div key={a.slug} className="bg-white rounded-xl border border-zinc-200 p-4 flex items-center gap-4" data-testid={`article-row-${a.slug}`}>
                  {a.image && <img src={a.image} alt="" className="w-16 h-12 object-cover rounded-lg bg-zinc-50" />}
                  <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-sm text-zinc-950 truncate">{a.title}</h2>
                    <p className="text-xs text-zinc-500 truncate">{a.excerpt}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setArticleForm({ data: { title: a.title, excerpt: a.excerpt, image: a.image, content: a.content, meta_description: a.meta_description || "" }, slug: a.slug })} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-600" aria-label="Editar" data-testid={`edit-article-${a.slug}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setToDelete({ type: "articles", slug: a.slug, name: a.title })} className="p-2 rounded-lg hover:bg-red-50 text-red-500" aria-label="Eliminar" data-testid={`delete-article-${a.slug}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* PRODUCT FORM */}
      <Dialog open={!!productForm} onOpenChange={(open) => !open && setProductForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{productForm?.slug ? "Editar producto" : "Nuevo producto"}</DialogTitle>
          </DialogHeader>
          {productForm && (
            <form onSubmit={saveProduct} className="space-y-4">
              <div className="space-y-1.5">
                <label className={labelCls}>Nombre *</label>
                <input required value={productForm.data.name} onChange={(e) => setProductForm({ ...productForm, data: { ...productForm.data, name: e.target.value } })} className={inputCls} data-testid="product-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className={labelCls}>Categoría *</label>
                  <select value={productForm.data.category} onChange={(e) => setProductForm({ ...productForm, data: { ...productForm.data, category: e.target.value } })} className={inputCls} data-testid="product-form-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>Línea</label>
                  <input value={productForm.data.line} onChange={(e) => setProductForm({ ...productForm, data: { ...productForm.data, line: e.target.value } })} placeholder="Ultra-Fresh, AZOO 9&1…" className={inputCls} data-testid="product-form-line" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className={labelCls}>Presentación</label>
                  <input value={productForm.data.presentation} onChange={(e) => setProductForm({ ...productForm, data: { ...productForm.data, presentation: e.target.value } })} placeholder="Ej. 600 g" className={inputCls} data-testid="product-form-presentation" />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>Orden</label>
                  <input type="number" value={productForm.data.order} onChange={(e) => setProductForm({ ...productForm, data: { ...productForm.data, order: Number(e.target.value) } })} className={inputCls} data-testid="product-form-order" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Imagen</label>
                <ImageField value={productForm.data.image} onChange={(url) => setProductForm({ ...productForm, data: { ...productForm.data, image: url } })} testid="product-form" />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Descripción (usa líneas con "- " para viñetas)</label>
                <textarea rows={5} value={productForm.data.description} onChange={(e) => setProductForm({ ...productForm, data: { ...productForm.data, description: e.target.value } })} className={inputCls} data-testid="product-form-description" />
              </div>
              <button type="submit" className="w-full px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold text-sm uppercase tracking-wider transition-colors" data-testid="product-form-save">
                Guardar producto
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ARTICLE FORM */}
      <Dialog open={!!articleForm} onOpenChange={(open) => !open && setArticleForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="article-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{articleForm?.slug ? "Editar artículo" : "Nuevo artículo"}</DialogTitle>
          </DialogHeader>
          {articleForm && (
            <form onSubmit={saveArticle} className="space-y-4">
              <div className="space-y-1.5">
                <label className={labelCls}>Título *</label>
                <input required value={articleForm.data.title} onChange={(e) => setArticleForm({ ...articleForm, data: { ...articleForm.data, title: e.target.value } })} className={inputCls} data-testid="article-form-title" />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Resumen</label>
                <textarea rows={2} value={articleForm.data.excerpt} onChange={(e) => setArticleForm({ ...articleForm, data: { ...articleForm.data, excerpt: e.target.value } })} className={inputCls} data-testid="article-form-excerpt" />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Meta descripción (SEO)</label>
                <textarea rows={2} value={articleForm.data.meta_description} onChange={(e) => setArticleForm({ ...articleForm, data: { ...articleForm.data, meta_description: e.target.value } })} className={inputCls} data-testid="article-form-meta" />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Imagen</label>
                <ImageField value={articleForm.data.image} onChange={(url) => setArticleForm({ ...articleForm, data: { ...articleForm.data, image: url } })} testid="article-form" />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Contenido (usa "## " para títulos y "- " para listas)</label>
                <textarea rows={10} value={articleForm.data.content} onChange={(e) => setArticleForm({ ...articleForm, data: { ...articleForm.data, content: e.target.value } })} className={inputCls} data-testid="article-form-content" />
              </div>
              <button type="submit" className="w-full px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold text-sm uppercase tracking-wider transition-colors" data-testid="article-form-save">
                Guardar artículo
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-500" data-testid="delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
